import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useVariables } from '../../../../providers/VariableProvider'
import Select from '../../../molecules/Select'
import CustomDateModal from './CustomDateModal'

dayjs.extend(utc)

const CUSTOM_VALUE = 'custom'
const STORAGE_KEY_PREFIX = 'ddpQuickDate.'
const DISPLAY_SEPARATOR = ' - '

export const options = [
  { label: 'Previous Day', value: 'day' },
  { label: 'Week to Date', value: 'wtd' },
  { label: 'Month to Date', value: 'mtd' },
  { label: 'Quarter to Date', value: 'qtd' },
  { label: 'Year to Date', value: 'ytd' },
  { label: 'Year', value: 'one year' },
  { label: '2 Years', value: 'two year' },
  { label: '3 Years', value: 'three year' },
  { label: '4 Years', value: 'four year' },
  { label: '5 Years', value: 'five year' },
  { label: '6 Years', value: 'six year' },
  { label: '7 Years', value: 'seven year' },
  { label: '8 Years', value: 'eight year' },
  { label: '9 Years', value: 'nine year' },
  { label: '10 Years', value: 'ten year' },
  { label: 'Since Inception', value: 'inception' },
  { label: 'Holding Period', value: 'holding period' },
  { label: 'Custom', value: CUSTOM_VALUE }
]

const standardValues = new Set(options.map(o => o.value))

const formatCustomDateValue = (startISO, endISO, dateFormat) => {
  return `${dayjs.utc(startISO).format(dateFormat)}${DISPLAY_SEPARATOR}${dayjs.utc(endISO).format(dateFormat)}`
}

const persistCustomDates = (applyTo, startISO, endISO) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${applyTo}`, `${startISO}|${endISO}`)
  } catch { /* localStorage unavailable */ }
}

const loadCustomDates = (applyTo) => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${applyTo}`)
    if (!stored) return null
    const [startDate, endDate] = stored.split('|')
    if (!startDate || !endDate) return null
    return { startDate, endDate }
  } catch {
    return null
  }
}

const clearCustomDates = (applyTo) => {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${applyTo}`)
  } catch { /* localStorage unavailable */ }
}

function PipelineQuickDatePicker ({ applyTo, defaultValue, className, availableDateRanges, excludeCustomDateRange, style, dateFormat }) {
  const { setVariable, getVariable } = useVariables()
  const [modalOpen, setModalOpen] = useState(false)
  const [customDates, setCustomDates] = useState(() => loadCustomDates(applyTo))

  const currentValue = getVariable(applyTo, defaultValue)
  const isCustomDateValue = currentValue && !standardValues.has(currentValue)

  useEffect(() => {
    if (getVariable(applyTo) === undefined) {
      const stored = loadCustomDates(applyTo)
      if (stored) {
        setVariable(applyTo, formatCustomDateValue(stored.startDate, stored.endDate, dateFormat))
      } else {
        setVariable(applyTo, defaultValue)
      }
    }
  }, [setVariable, getVariable, applyTo, defaultValue, dateFormat])

  const setValue = useCallback((value) => {
    if (value === CUSTOM_VALUE) {
      setModalOpen(true)
      return
    }
    clearCustomDates(applyTo)
    setCustomDates(null)
    setVariable(applyTo, value)
  }, [setVariable, applyTo])

  const handleModalSubmit = useCallback((startISO, endISO) => {
    const formatted = formatCustomDateValue(startISO, endISO, dateFormat)
    setVariable(applyTo, formatted)
    persistCustomDates(applyTo, startISO, endISO)
    setCustomDates({ startDate: startISO, endDate: endISO })
    setModalOpen(false)
  }, [setVariable, applyTo, dateFormat])

  const handleModalClose = useCallback(() => {
    setModalOpen(false)
  }, [])

  const dateRangeOptions = useMemo(() => {
    return options.filter(option => {
      if (excludeCustomDateRange && option.value === CUSTOM_VALUE) return false
      return availableDateRanges ? availableDateRanges.includes(option.value) : true
    }).map(option => {
      if (option.value === CUSTOM_VALUE && isCustomDateValue) {
        return { ...option, displayLabel: currentValue }
      }
      return option
    })
  }, [availableDateRanges, excludeCustomDateRange, isCustomDateValue, currentValue])

  const selectValue = isCustomDateValue ? CUSTOM_VALUE : currentValue

  return (
    <>
      <Select
        className={className}
        style={style}
        options={dateRangeOptions}
        value={selectValue}
        onChange={setValue}
      />
      <CustomDateModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        dateFormat={dateFormat}
        initialStartDate={customDates?.startDate}
        initialEndDate={customDates?.endDate}
      />
    </>
  )
}

PipelineQuickDatePicker.propTypes = {
  applyTo: PropTypes.string,
  defaultValue: PropTypes.string,
  className: PropTypes.string,
  availableDateRanges: PropTypes.arrayOf(PropTypes.string),
  excludeCustomDateRange: PropTypes.bool,
  style: PropTypes.object,
  dateFormat: PropTypes.string
}

PipelineQuickDatePicker.defaultProps = {
  applyTo: 'quickDate',
  defaultValue: 'ytd',
  excludeCustomDateRange: false,
  dateFormat: 'MM/DD/YYYY'
}

export default PipelineQuickDatePicker
